import componentRules from "./rules/generated/component-rules.json";
import designRules from "./rules/generated/design-rules.json";
import layoutRules from "./rules/generated/layout-rules.json";
import pageRules from "./rules/generated/page-rules.json";
import mediaRules from "./rules/generated/media-rules.json";
import pageRecipes from "./pages/page-recipes.json";
import { actionEmitter, EmitEvent } from "../config/ActionEmitter";
import { ScriptIncludeType, TemplateType, VersionStatus } from "../config/Enums";
import { AddUpdatePageCommand } from "../models/content/AddUpdatePageCommand";
import { IContentComponent, IPage, ISiteDetails, IVersion, IVersionSection } from "../models/Interfaces";
import { ContentEditorService } from "../services/ContentEditorService";
import { ContentService } from "../services/ContentService";
import { MediaService } from "../services/MediaService";
import { SiteService } from "../services/SiteService";
import { ScriptAndStyleService } from "../services/ScriptAndStyleService";
import { buildPageDraft } from "./pageBlueprint";
import { isSiteAiConnected, isSiteAiImageConnected } from "./siteAi";
import { IAIDraftSection, IAIMediaRequest, IAIPageCreationBrief, IAIPageDraft, IAISectionCreationBrief, IAIStandardDraftResponse } from "./types";

interface IGenerationResult {
  draft: IAIPageDraft;
  version: IVersion;
}

interface ISectionGenerationResult {
  section: IVersionSection | null;
  draftSection: IAIDraftSection;
}

interface ISiteContext {
  siteName: string;
  description: string;
  about: string;
  defaultLanguage: string;
  domain: string;
  subDomain: string;
  baseUrl: string;
  publishedPages: Array<{
    title: string;
    url: string;
    language: string;
    publishedVersionCount: number;
  }>;
  styleContext: {
    enabledCssAssets: Array<{
      description: string;
      url: string;
      classSelectors: string[];
      sectionCandidateClasses: string[];
      cssVariables: Record<string, string>;
      hasGlobalOverrides: boolean;
      codeSnippet: string;
    }>;
    enabledClientScripts: Array<{
      description: string;
      url: string;
      codeSnippet: string;
    }>;
    guidance: string[];
  };
}

type ProgressCallback = (message: string) => void;

const isColumnTemplate = (template: string): boolean =>
  template === TemplateType.ColumnsCC || template === TemplateType.ColumnsFW;

const mapTemplate = (template: string): TemplateType => {
  switch (template) {
    case TemplateType.ColumnsFW:
      return TemplateType.ColumnsFW;
    case TemplateType.ContentCC:
      return TemplateType.ContentCC;
    case TemplateType.ContentFW:
      return TemplateType.ContentFW;
    case TemplateType.ColumnsCC:
    default:
      return TemplateType.ColumnsCC;
  }
};

const sectionSkeleton = (version: IVersion, template: string, columns: number, order: number, backgroundColour?: string | null, sectionClass?: string | null): IVersionSection => ({
  Id: 0,
  ContentComponents: [],
  PageVersion: version,
  Name: undefined,
  Order: order,
  ClientScript: undefined,
  JSFilePath: undefined,
  Template: mapTemplate(template),
  Columns: columns,
  PageVersionId: version.Id,
  BackgroundColour: backgroundColour ?? undefined,
  BackgroundImage: undefined,
  SectionHRefId: undefined,
  SectionClass: sectionClass ?? undefined,
  DynamicComponents: undefined,
  ExtendedProperties: undefined,
  IsPreview: false
});

const cloneWithPropertyValues = (component: IContentComponent, values: Array<{ propertyName: string; value: string }>): IContentComponent => ({
  ...component,
  BaseComponent: {
    ...component.BaseComponent,
    Properties: component.BaseComponent.Properties.map((property) => {
      const update = values.find((value) => value.propertyName === property.PropertyName);

      return update
        ? {
            ...property,
            PublishedValue: update.value
          }
        : property;
    })
  }
});

export class AIContentGenerationService {
  private readonly contentService = new ContentService();
  private readonly contentEditorService = new ContentEditorService();
  private readonly mediaService = new MediaService();
  private readonly siteService = new SiteService();
  private readonly scriptAndStyleService = new ScriptAndStyleService();

  private readonly standardDraftInstructions = [
    "Return a single JSON object matching the supplied schema.",
    "Do not wrap the JSON in markdown.",
    "Use only the provided layouts, components, and editable properties.",
    "Keep the response compatible with the CMS draft contract so it can be saved without manual remapping.",
    "This should read and feel like a designed marketing page, not a plain document.",
    "Use stronger visual hierarchy across sections.",
    "Prefer concise headlines and avoid long body copy in cards or repetitive feature sections.",
    "When a component exposes class, colour, or container styling properties, use them deliberately to create contrast and rhythm.",
    "Every section must set backgroundColour to a hex value appropriate for its visual role — hero dark, features light or null, CTA an accent distinct from the hero. Only set null when the site default is intentionally correct for that section.",
    "Hero sections must have a dark or brand-primary backgroundColour (e.g. #111827 or #1a2b3c) and sectionClass sb-hero. Any Banner component inside a hero section must also have its BackColour property set to the same dark value — never leave Banner BackColour empty inside a dark section or it will render white over the dark background.",
    "CTA sections must have a backgroundColour that visually contrasts with the hero and sectionClass sb-cta-band. Any Banner component inside a CTA section must have its BackColour set to the same accent value.",
    "Feature grid sections should use sectionClass sb-feature-grid and a light or neutral backgroundColour.",
    "In HTML editor properties (authoringType: rich-text-html), apply Bootstrap 5 display classes — display-1 or display-2 for hero headlines, display-4 or display-5 for section headings, and the lead class on supporting intro paragraphs.",
    "Banner component TextColour must always be set when BackColour is set — never leave text colour unspecified on a coloured background.",
    "Card component titles must be under 8 words and body text under 25 words — cards are scannable grid items, not paragraphs.",
    "For each mediaRequest that maps to a specific component image property, set targetSectionIndex (0-based section position), targetComponentIndex (0-based position within that section), and targetPropertyName to the exact property name that should receive the image URL (e.g. ImageUrl, BackgroundImage). Set all three to null only if the image has no specific component target."
  ].join(" ");

  private buildSiteBaseUrl = (domain?: string | null, subDomain?: string | null): string => {
    const trimmedDomain = (domain ?? "").trim();
    const trimmedSubDomain = (subDomain ?? "").trim();

    if (trimmedDomain.length > 0) {
      return /^https?:\/\//i.test(trimmedDomain) ? trimmedDomain : `https://${trimmedDomain}`;
    }

    if (trimmedSubDomain.length > 0) {
      return `https://${trimmedSubDomain}`;
    }

    return "";
  };

  private extractCssClassSelectors = (cssCode?: string | null): string[] => {
    if (!cssCode) {
      return [];
    }

    const matches = cssCode.match(/(?<![a-zA-Z0-9_-])\.([a-zA-Z_-][a-zA-Z0-9_-]*)/g) ?? [];
    return [...new Set(matches.map((match) => match.slice(1)))].slice(0, 40);
  };

  private hasGlobalCssOverrides = (cssCode?: string | null): boolean => {
    if (!cssCode) {
      return false;
    }

    return /(body|html|:root|\.cms-|main|header|footer|\*|\[class)/i.test(cssCode);
  };

  private compactCodeSnippet = (code?: string | null, maxLength: number = 2000): string => {
    if (!code) {
      return "";
    }

    const compacted = code.replace(/\s+/g, " ").trim();
    return compacted.length > maxLength ? `${compacted.slice(0, maxLength)}...` : compacted;
  };

  private extractCssVariables = (cssCode?: string | null): Record<string, string> => {
    if (!cssCode) return {};

    const rootMatch = cssCode.match(/:root\s*\{([^}]+)\}/);
    if (!rootMatch) return {};

    const variables: Record<string, string> = {};
    const varPattern = /(--[a-zA-Z0-9_-]+)\s*:\s*([^;]+);/g;
    let match;

    while ((match = varPattern.exec(rootMatch[1])) !== null) {
      variables[match[1].trim()] = match[2].trim();
    }

    return variables;
  };

  private extractSectionCandidateClasses = (classSelectors: string[]): string[] => {
    return classSelectors.filter(cls =>
      /^(hero|banner|section|bg-|dark|light|cta|feature|card|header|intro|highlight|primary|secondary|accent)/i.test(cls)
    );
  };

  private extractExistingContent = (sections: IVersionSection[], source: "published-version" | "current-version") => {
    const TEXT_TYPES = new Set(["String", "BodyText"]);
    const MAX_VALUE_LENGTH = 300;

    const contentSections = sections
      .filter(s => s.ContentComponents.length > 0)
      .map(s => ({
        name: s.Name ?? "Section",
        components: s.ContentComponents
          .map(c => {
            const properties: Record<string, string> = {};

            for (const prop of c.BaseComponent.Properties) {
              if (prop.UserEditable && TEXT_TYPES.has(prop.Type.Name) && prop.PublishedValue?.trim()) {
                const value = prop.PublishedValue.trim();
                properties[prop.PropertyName] = value.length > MAX_VALUE_LENGTH
                  ? `${value.slice(0, MAX_VALUE_LENGTH)}…`
                  : value;
              }
            }

            return Object.keys(properties).length > 0
              ? { name: c.BaseComponent.Name, properties }
              : null;
          })
          .filter((c): c is NonNullable<typeof c> => c !== null)
      }))
      .filter(s => s.components.length > 0);

    if (contentSections.length === 0) return null;

    return {
      source,
      note: "Use this as a reference for tone, brand voice, and factual details. Do not copy verbatim — use it to inform the new version's language and keep content consistent with established copy.",
      sections: contentSections
    };
  };

  private resolveOrCreateAiFolder = async (): Promise<number | null> => {
    const folders = await this.mediaService.getSiteFolders();
    const existing = folders.find(f => f.FolderName.toLowerCase() === "ai generated" && f.ParentId === null);

    if (existing) return existing.Id;

    const newFolder = this.mediaService.newSiteFolder(null);
    if (!newFolder) return null;

    newFolder.FolderName = "AI Generated";
    const saved = await this.mediaService.saveFolder(newFolder);
    return saved?.Id ?? null;
  };

  private generateAndUploadImage = async (prompt: string, folderId: number, apiKey: string, endpoint: string, imageModel: string): Promise<string | null> => {
    const response = await fetch(`${endpoint}/images/generations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`
      },
      body: JSON.stringify({ model: imageModel, prompt, n: 1, size: "1024x1024", response_format: "b64_json" })
    });

    if (!response.ok) return null;

    const data = await response.json();
    const imageData = data?.data?.[0];
    if (!imageData?.b64_json) return null;

    const binary = atob(imageData.b64_json);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: "image/png" });
    const filename = `ai-${Date.now()}.png`;

    if (!blob) return null;

    const file = new File([blob], filename, { type: blob.type });
    const uploaded = await this.mediaService.uploadFile(file, folderId);
    return uploaded?.Url ?? null;
  };

  private generateImages = async (
    mediaRequests: IAIMediaRequest[],
    siteDetails: ISiteDetails,
    onProgress?: ProgressCallback
  ): Promise<Array<{ sectionIndex: number; componentIndex: number; propertyName: string; url: string }>> => {
    const results: Array<{ sectionIndex: number; componentIndex: number; propertyName: string; url: string }> = [];

    const targeted = mediaRequests.filter(r =>
      r.targetSectionIndex !== null && r.targetComponentIndex !== null && r.targetPropertyName !== null
    );

    if (targeted.length === 0) return results;

    const folderId = await this.resolveOrCreateAiFolder();
    if (!folderId) return results;

    const endpoint = (siteDetails.AIEndpoint ?? "https://api.openai.com/v1").replace(/\/$/, "");
    const apiKey = siteDetails.AIApiKey!;
    const imageModel = siteDetails.AIImageModel!;

    for (const request of targeted) {
      onProgress?.(`Generating image: ${request.purpose}`);

      const url = await this.generateAndUploadImage(request.prompt, folderId, apiKey, endpoint, imageModel);

      if (url) {
        results.push({
          sectionIndex: request.targetSectionIndex!,
          componentIndex: request.targetComponentIndex!,
          propertyName: request.targetPropertyName!,
          url
        });
      }
    }

    return results;
  };

  private applyImagesToDraft = (
    draft: IAIPageDraft,
    images: Array<{ sectionIndex: number; componentIndex: number; propertyName: string; url: string }>
  ): IAIPageDraft => {
    if (images.length === 0) return draft;

    return {
      ...draft,
      sections: draft.sections.map((section, sectionIndex) => ({
        ...section,
        components: section.components.map((component, componentIndex) => {
          const image = images.find(img => img.sectionIndex === sectionIndex && img.componentIndex === componentIndex);
          if (!image) return component;

          const existingIndex = component.propertyValues.findIndex(p => p.propertyName === image.propertyName);
          const updatedValues = [...component.propertyValues];

          if (existingIndex !== -1) {
            updatedValues[existingIndex] = { propertyName: image.propertyName, value: image.url };
          } else {
            updatedValues.push({ propertyName: image.propertyName, value: image.url });
          }

          return { ...component, propertyValues: updatedValues };
        })
      }))
    };
  };

  private fetchExistingPageContent = async (page: IPage): Promise<ReturnType<typeof this.extractExistingContent>> => {
    const latestPublished = (page.Versions ?? [])
      .filter(v => v.Status === VersionStatus.Published)
      .sort((a, b) => b.Id - a.Id)[0];

    if (!latestPublished) return null;

    const sections = await this.contentService.getVersionSections(latestPublished.Id);
    return this.extractExistingContent(sections, "published-version");
  };

  private buildSiteContext = async (): Promise<ISiteContext> => {
    const [siteDetails, pages, cssAssets, clientScripts] = await Promise.all([
      this.siteService.getSiteDetails(),
      this.contentService.getAllPages(),
      this.scriptAndStyleService.getCss(),
      this.scriptAndStyleService.getScriptByType(ScriptIncludeType.ClientScript)
    ]);

    const publishedPages = (pages ?? [])
      .map((page) => ({
        title: page.Title ?? page.Alias ?? page.Url ?? "Untitled page",
        url: page.Url ?? "/",
        language: page.Language ?? "",
        publishedVersionCount: (page.Versions ?? []).filter((version) => version.Status === VersionStatus.Published).length
      }))
      .filter((page) => page.publishedVersionCount > 0)
      .slice(0, 20);

    const enabledCssAssets = (cssAssets ?? [])
      .filter((asset) => asset.Enabled)
      .sort((a, b) => (a.LoadOrder ?? 0) - (b.LoadOrder ?? 0))
      .map((asset) => {
        const classSelectors = this.extractCssClassSelectors(asset.Code);
        return {
          description: asset.Description ?? "",
          url: asset.Url ?? "",
          classSelectors,
          sectionCandidateClasses: this.extractSectionCandidateClasses(classSelectors),
          cssVariables: this.extractCssVariables(asset.Code),
          hasGlobalOverrides: this.hasGlobalCssOverrides(asset.Code),
          codeSnippet: this.compactCodeSnippet(asset.Code)
        };
      })
      .slice(0, 10);

    const enabledClientScripts = (clientScripts ?? [])
      .filter((asset) => asset.Enabled)
      .sort((a, b) => (a.LoadOrder ?? 0) - (b.LoadOrder ?? 0))
      .map((asset) => ({
        description: asset.Description ?? "",
        url: asset.Url ?? "",
        codeSnippet: this.compactCodeSnippet(asset.Code, 400)
      }))
      .slice(0, 8);

    return {
      siteName: siteDetails?.SiteName ?? "",
      description: siteDetails?.Description ?? "",
      about: siteDetails?.About ?? "",
      defaultLanguage: siteDetails?.DefaultLanguage ?? "",
      domain: siteDetails?.Domain ?? "",
      subDomain: siteDetails?.SubDomain ?? "",
      baseUrl: this.buildSiteBaseUrl(siteDetails?.Domain, siteDetails?.SubDomain),
      publishedPages,
      styleContext: {
        enabledCssAssets,
        enabledClientScripts,
        guidance: [
          "Uploaded site CSS and client scripts are the primary design system context for this site.",
          "Prefer reusing existing class selectors from enabled site CSS when they clearly fit the generated page.",
          "Use page-level PageStyle and PageScript only for small page-specific deviations or additions.",
          "If enabled site CSS includes global overrides or cms-specific classes, keep generated component and section classes compatible with that styling approach."
        ]
      }
    };
  };

  private buildDraftSchema = () => ({
    name: "cms_page_draft",
    strict: true,
    schema: {
      type: "object",
      additionalProperties: false,
      required: [
        "pageTitle",
        "pageUrl",
        "pageKind",
        "audience",
        "goal",
        "tone",
        "parentPageUrl",
        "summaryPrompt",
        "sections",
        "mediaRequests"
      ],
      properties: {
        pageTitle: { type: "string" },
        pageUrl: { type: "string" },
        pageKind: { type: "string" },
        audience: { type: "string" },
        goal: { type: "string" },
        tone: { type: "string" },
        parentPageUrl: {
          type: ["string", "null"]
        },
        summaryPrompt: { type: "string" },
        sections: {
          type: "array",
          minItems: 1,
          items: {
            type: "object",
            additionalProperties: false,
            required: ["name", "template", "columns", "purpose", "backgroundColour", "sectionClass", "components"],
            properties: {
              name: { type: "string" },
              template: {
                type: "string",
                enum: (layoutRules.layouts ?? []).map((layout) => layout.templateName)
              },
              columns: { type: "integer" },
              purpose: { type: "string" },
              backgroundColour: { type: ["string", "null"] },
              sectionClass: { type: ["string", "null"] },
              components: {
                type: "array",
                minItems: 1,
                items: {
                  type: "object",
                  additionalProperties: false,
                  required: ["componentName", "displayName", "purpose", "propertyValues"],
                  properties: {
                    componentName: {
                      type: "string",
                      enum: (componentRules.components ?? []).map((component) => component.name)
                    },
                    displayName: { type: "string" },
                    purpose: { type: "string" },
                    propertyValues: {
                      type: "array",
                      items: {
                        type: "object",
                        additionalProperties: false,
                        required: ["propertyName", "value"],
                        properties: {
                          propertyName: { type: "string" },
                          value: { type: "string" }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        },
        mediaRequests: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            required: ["kind", "purpose", "prompt", "recommendedFolder", "targetSectionIndex", "targetComponentIndex", "targetPropertyName"],
            properties: {
              kind: { type: "string" },
              purpose: { type: "string" },
              prompt: { type: "string" },
              recommendedFolder: { type: "string" },
              targetSectionIndex: { type: ["integer", "null"] },
              targetComponentIndex: { type: ["integer", "null"] },
              targetPropertyName: { type: ["string", "null"] }
            }
          }
        }
      }
    }
  });

  private extractOutputText = (responseData: any): string => {
    const messages = Array.isArray(responseData?.output) ? responseData.output : [];

    return messages
      .flatMap((item: any) => Array.isArray(item?.content) ? item.content : [])
      .filter((content: any) => content?.type === "output_text" && typeof content?.text === "string")
      .map((content: any) => content.text)
      .join("\n")
      .trim();
  };

  private normaliseDraftResponse = (responseDraft: IAIStandardDraftResponse, draftSeed: IAIPageDraft): IAIPageDraft => ({
    pageId: draftSeed.pageId,
    versionId: draftSeed.versionId,
    pageTitle: responseDraft.pageTitle?.trim() || draftSeed.pageTitle,
    pageUrl: responseDraft.pageUrl?.trim() || draftSeed.pageUrl,
    pageKind: responseDraft.pageKind?.trim() || draftSeed.pageKind,
    audience: responseDraft.audience?.trim() || draftSeed.audience,
    goal: responseDraft.goal?.trim() || draftSeed.goal,
    tone: responseDraft.tone?.trim() || draftSeed.tone,
    parentPageUrl: responseDraft.parentPageUrl ?? draftSeed.parentPageUrl ?? null,
    summaryPrompt: responseDraft.summaryPrompt?.trim() || draftSeed.summaryPrompt,
    sections: Array.isArray(responseDraft.sections) ? responseDraft.sections : draftSeed.sections,
    mediaRequests: Array.isArray(responseDraft.mediaRequests) ? responseDraft.mediaRequests : draftSeed.mediaRequests
  });

  private validateDraft = (draft: IAIPageDraft): IAIPageDraft => {
    const componentMap = new Map((componentRules.components ?? []).map((component) => [component.name, component]));
    const validLayouts = new Set((layoutRules.layouts ?? []).map((layout) => layout.templateName));

    draft.sections = (Array.isArray(draft.sections) ? draft.sections : [])
      .filter((section) => validLayouts.has(section.template))
      .map((section) => ({
        ...section,
        name: section.name?.trim() || "Generated section",
        purpose: section.purpose?.trim() || "Generated content section",
        columns: Number.isFinite(section.columns) && section.columns > 0 ? section.columns : 1,
        components: section.components
          .filter((component) => componentMap.has(component.componentName))
          .map((component) => {
            const rule = componentMap.get(component.componentName);
            const validProperties = new Set((rule?.editableProperties ?? []).map((property: any) => property.propertyName));

            return {
              ...component,
              displayName: component.displayName?.trim() || component.componentName,
              purpose: component.purpose?.trim() || "Generated component",
              propertyValues: component.propertyValues.filter((property) => validProperties.has(property.propertyName))
            };
          })
      }))
      .filter((section) => section.components.length > 0);

    return draft;
  };

  private buildSingleSectionSeed = (pageDetails: AddUpdatePageCommand, versionId: number, brief: IAISectionCreationBrief): IAIPageDraft => {
    const sectionMap: Record<string, IAIDraftSection> = {
      hero: {
        name: "Hero",
        template: TemplateType.ColumnsFW,
        columns: 1,
        purpose: "Hero section with a strong introduction and call to action",
        components: [
          {
            componentName: "Banner",
            displayName: "Banner",
            purpose: "High-impact opening statement",
            propertyValues: []
          }
        ]
      },
      intro: {
        name: "Overview",
        template: TemplateType.ColumnsCC,
        columns: 1,
        purpose: "Introductory section that explains the next key message",
        components: [
          {
            componentName: "TitleAndText",
            displayName: "Title And Text",
            purpose: "Intro copy block",
            propertyValues: []
          }
        ]
      },
      "feature-grid": {
        name: "Features",
        template: TemplateType.ColumnsCC,
        columns: 3,
        purpose: "Feature or benefit grid",
        components: [
          { componentName: "Card", displayName: "Card", purpose: "Feature card", propertyValues: [] },
          { componentName: "Card", displayName: "Card", purpose: "Feature card", propertyValues: [] },
          { componentName: "Card", displayName: "Card", purpose: "Feature card", propertyValues: [] }
        ]
      },
      cta: {
        name: "Call To Action",
        template: TemplateType.ColumnsFW,
        columns: 1,
        purpose: "Distinct closing call to action",
        components: [
          {
            componentName: "Banner",
            displayName: "Banner",
            purpose: "CTA banner",
            propertyValues: []
          }
        ]
      },
      faq: {
        name: "FAQ",
        template: TemplateType.ColumnsCC,
        columns: 1,
        purpose: "Frequently asked questions",
        components: [
          {
            componentName: "Accordion",
            displayName: "Accordion",
            purpose: "FAQ accordion",
            propertyValues: []
          }
        ]
      },
      testimonial: {
        name: "Proof",
        template: TemplateType.ColumnsCC,
        columns: 1,
        purpose: "Proof or testimonial section",
        components: [
          {
            componentName: "TitleAndText",
            displayName: "Title And Text",
            purpose: "Proof statement",
            propertyValues: []
          }
        ]
      },
      custom: {
        name: "Generated Section",
        template: TemplateType.ColumnsCC,
        columns: 1,
        purpose: "Custom generated section",
        components: [
          {
            componentName: "TitleAndText",
            displayName: "Title And Text",
            purpose: "Generated content block",
            propertyValues: []
          }
        ]
      }
    };

    const selectedSection = sectionMap[brief.sectionKind] ?? sectionMap.custom;

    return {
      pageId: pageDetails.Id,
      versionId,
      pageTitle: pageDetails.Title || "Untitled page",
      pageUrl: pageDetails.Url || "/",
      pageKind: "single-section",
      audience: brief.targetAudience,
      goal: brief.primaryGoal,
      tone: brief.tone,
      parentPageUrl: pageDetails.ParentUrl || null,
      summaryPrompt: brief.contentInstructions,
      sections: [selectedSection],
      mediaRequests: []
    };
  };

  private toPageBrief = (brief: IAISectionCreationBrief): IAIPageCreationBrief => ({
    pageKind: "single-section",
    primaryGoal: brief.primaryGoal,
    targetAudience: brief.targetAudience,
    tone: brief.tone,
    keySections: brief.sectionKind,
    contentInstructions: brief.contentInstructions
  });

  private generateDraftWithOpenAI = async (draftSeed: IAIPageDraft, brief: IAIPageCreationBrief, existingContent?: ReturnType<typeof this.extractExistingContent>): Promise<IAIPageDraft | null> => {
    const [siteDetails, siteContext] = await Promise.all([
      this.siteService.getSiteDetails(),
      this.buildSiteContext()
    ]);

    if (!isSiteAiConnected(siteDetails)) {
      return null;
    }

    const provider = (siteDetails.AIProvider ?? "").toLowerCase();

    if (provider && provider !== "openai" && provider !== "xai") {
      actionEmitter.emit(EmitEvent.Result, false, `AI provider '${siteDetails.AIProvider}' is not wired yet. Falling back to the local draft generator.`);
      return null;
    }

    const endpoint = (
      siteDetails.AIEndpoint
      ?? (provider === "xai" ? "" : "https://api.openai.com/v1")
    ).replace(/\/$/, "");

    if (!endpoint) {
      throw new Error(`AI endpoint is required for provider '${siteDetails.AIProvider}'.`);
    }

    const payload = {
      model: siteDetails.AIModel,
      input: [
        {
          role: "developer",
          content: [
            {
              type: "input_text",
              text: [
                "You generate SmoothBadger CMS page drafts for the admin app.",
                "Keep the overall section/component structure close to the supplied draft seed unless the brief clearly suggests a small improvement.",
                "Treat the rules as both a structural and presentation contract.",
                "Aim for a premium, structured, high-contrast marketing page with a strong hero, a disciplined feature grid, and a distinct closing CTA.",
                "Each section carries backgroundColour and sectionClass fields — always populate these based on the section's visual role: hero sections dark, feature sections light or neutral, CTA sections a distinct accent. Set sectionClass from the preferred named classes in the design rules (sb-hero, sb-feature-grid, sb-proof-strip, sb-cta-band).",
                "In HTML editor properties (authoringType: rich-text-html), apply Bootstrap 5 display classes: display-1 or display-2 for hero headlines, display-4 or display-5 for section headings, and the lead class on supporting intro paragraphs.",
                "Treat the contentInstructions field as the primary creative brief. The other brief fields are supporting constraints and hints.",
                "Use the supplied siteContext to keep the page in context for the site and avoid content that feels disconnected from the rest of the website.",
                "Review siteContext.styleContext for site CSS context — prefer sectionCandidateClasses for SectionClass values where available, and use cssVariables colour values when setting backgroundColour and component colour properties.",
                "Treat uploaded site CSS and scripts as the shared site design system. PageStyle and PageScript should only be used for small page-specific refinements when necessary.",
                this.standardDraftInstructions
              ].join(" ")
            }
          ]
        },
        {
          role: "user",
          content: [
            {
              type: "input_text",
              text: JSON.stringify({
                brief,
                primaryBrief: brief.contentInstructions,
                briefPriority: {
                  primary: "contentInstructions",
                  supporting: ["pageKind", "primaryGoal", "targetAudience", "tone", "keySections"]
                },
                siteContext,
                ...(existingContent ? { existingContent } : {}),
                draftSeed,
                designIntent: {
                  visualDirection: "premium structured marketing page with clear visual hierarchy",
                  sectionContrast: "high — hero is dark, feature sections are light or neutral, CTA is a distinct accent",
                  cardCopyLength: "title under 8 words, body under 25 words",
                  heroTreatment: "section backgroundColour dark (e.g. #111827), sectionClass sb-hero — Banner inside hero must also set BackColour to same dark value and TextColour to #ffffff — headline wrapped in <h1 class='display-1'> or <h2 class='display-2'> — supporting copy max 2 short sentences",
                  closingCtaTreatment: "section backgroundColour accent (e.g. #0d6efd) distinct from hero, sectionClass sb-cta-band — Banner inside CTA must also set BackColour to same accent and TextColour to #ffffff — single direct headline and one action, no long body copy",
                  sectionColourContract: {
                    hero: { backgroundColour: "dark hex e.g. #111827 or #1a2b3c", sectionClass: "sb-hero", textColour: "#ffffff" },
                    features: { backgroundColour: "#ffffff or #f8f9fa or null for site default", sectionClass: "sb-feature-grid", textColour: "default dark" },
                    cta: { backgroundColour: "accent hex different from hero e.g. #0d6efd or brand accent", sectionClass: "sb-cta-band", textColour: "#ffffff" },
                    intro: { backgroundColour: "null or #f8f9fa", sectionClass: null, textColour: "default dark" }
                  },
                  bootstrapTypographyContract: {
                    heroHeadline: "<h1 class='display-1'> or <h2 class='display-2'>",
                    sectionHeading: "<h2 class='display-5'> or <h3 class='display-6'>",
                    introText: "<p class='lead'>",
                    note: "apply only inside HTML editor properties with authoringType rich-text-html"
                  }
                },
                rules: {
                  pageRules,
                  layoutRules,
                  componentRules,
                  mediaRules,
                  designRules,
                  pageRecipes
                }
              })
            }
          ]
        }
      ],
      text: {
        format: {
          type: "json_schema",
          ...this.buildDraftSchema()
        }
      }
    };

    const response = await fetch(`${endpoint}/responses`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${siteDetails.AIApiKey}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const failureText = await response.text();
      throw new Error(`OpenAI request failed (${response.status}): ${failureText}`);
    }

    const responseData = await response.json();
    const outputText = this.extractOutputText(responseData);

    if (!outputText) {
      throw new Error("OpenAI returned no usable output text.");
    }

    const parsedDraft = JSON.parse(outputText) as IAIStandardDraftResponse;

    return this.validateDraft(this.normaliseDraftResponse(parsedDraft, draftSeed));
  };

  public generateVersionFromBrief = async (
    page: IPage,
    brief: IAIPageCreationBrief,
    onProgress?: ProgressCallback
  ): Promise<IGenerationResult | null> => {
    const pageDetails = AddUpdatePageCommand.MapFromModel(page);

    if (!pageDetails) {
      return null;
    }

    onProgress?.("Creating draft version");
    const [version, siteDetails] = await Promise.all([
      this.contentService.addVersion(page.Id),
      this.siteService.getSiteDetails()
    ]);

    if (!version) {
      return null;
    }

    onProgress?.("Loading component definitions");
    const [availableComponents, existingContent] = await Promise.all([
      this.contentService.getComponents(),
      this.fetchExistingPageContent(page)
    ]);

    const draftSeed = buildPageDraft({
      pageId: page.Id,
      versionId: version.Id,
      pageDetails,
      brief
    });
    onProgress?.("Generating content with connected AI");
    const aiDraft = await this.generateDraftWithOpenAI(draftSeed, brief, existingContent ?? undefined);

    if (!aiDraft) {
      throw new Error("AI generation is not available for this site. Check that AI is enabled and the provider settings are complete.");
    }

    let draft = aiDraft;

    if (draft.mediaRequests.length > 0) {
      if (isSiteAiImageConnected(siteDetails)) {
        const generatedImages = await this.generateImages(draft.mediaRequests, siteDetails, onProgress);
        draft = this.applyImagesToDraft(draft, generatedImages);
      } else {
        onProgress?.("Media prompts prepared; image generation not enabled for this site");
      }
    }

    for (let sectionIndex = 0; sectionIndex < draft.sections.length; sectionIndex += 1) {
      const draftSection = draft.sections[sectionIndex];
      onProgress?.(`Creating section ${sectionIndex + 1} of ${draft.sections.length}`);

      const savedSection = await this.contentEditorService.SaveSection(
        sectionSkeleton(version, draftSection.template, draftSection.columns, sectionIndex + 1, draftSection.backgroundColour, draftSection.sectionClass)
      );

      if (!savedSection) {
        continue;
      }

      for (let componentIndex = 0; componentIndex < draftSection.components.length; componentIndex += 1) {
        const draftComponent = draftSection.components[componentIndex];
        const definition = availableComponents.find((component) => component.Name === draftComponent.componentName);

        if (!definition) {
          continue;
        }

        onProgress?.(`Adding ${draftComponent.displayName}`);

        let addedComponent: IContentComponent | null = null;

        if (isColumnTemplate(savedSection.Template)) {
          const targetColumn = Math.min(componentIndex, Math.max(savedSection.Columns - 1, 0));
          addedComponent = await this.contentEditorService.AddCComponentToColumnSection(definition, savedSection.Id, targetColumn);
        } else {
          const y = componentIndex * 10;
          addedComponent = await this.contentEditorService.AddComponentToGridSection(savedSection.Id, definition, 0, y, 12, 10);
        }

        if (!addedComponent) {
          continue;
        }

        const componentWithValues = cloneWithPropertyValues(addedComponent, draftComponent.propertyValues);
        await this.contentEditorService.SaveContentComponent(componentWithValues);
      }
    }

    onProgress?.("Opening designer");
    return {
      draft,
      version
    };
  };

  public generateSingleSectionFromBrief = async (
    version: IVersion,
    insertOrder: number,
    brief: IAISectionCreationBrief,
    onProgress?: ProgressCallback
  ): Promise<ISectionGenerationResult | null> => {
    const page = version.Page ?? await this.contentService.getPageDetails(version.PageId);
    const pageDetails = page ? AddUpdatePageCommand.MapFromModel(page) : null;

    if (!pageDetails) {
      return null;
    }

    onProgress?.("Loading component definitions");
    const [availableComponents, existingSections, siteDetails] = await Promise.all([
      this.contentService.getComponents(),
      this.contentService.getVersionSections(version.Id),
      this.siteService.getSiteDetails()
    ]);

    const existingContent = this.extractExistingContent(existingSections, "current-version");

    const draftSeed = this.buildSingleSectionSeed(pageDetails, version.Id, brief);

    onProgress?.("Generating section with connected AI");
    const aiDraft = await this.generateDraftWithOpenAI(draftSeed, this.toPageBrief(brief), existingContent ?? undefined);

    if (!aiDraft || aiDraft.sections.length === 0) {
      throw new Error("AI did not return a usable section draft.");
    }

    let sectionDraft = aiDraft;

    if (sectionDraft.mediaRequests.length > 0) {
      if (isSiteAiImageConnected(siteDetails)) {
        const generatedImages = await this.generateImages(sectionDraft.mediaRequests, siteDetails, onProgress);
        sectionDraft = this.applyImagesToDraft(sectionDraft, generatedImages);
      } else {
        onProgress?.("Media prompts prepared; image generation not enabled for this site");
      }
    }

    const draftSection = sectionDraft.sections[0];

    onProgress?.("Creating section");
    const savedSection = await this.contentEditorService.SaveSection(
      sectionSkeleton(version, draftSection.template, draftSection.columns, insertOrder, draftSection.backgroundColour, draftSection.sectionClass)
    );

    if (!savedSection) {
      return null;
    }

    for (let componentIndex = 0; componentIndex < draftSection.components.length; componentIndex += 1) {
      const draftComponent = draftSection.components[componentIndex];
      const definition = availableComponents.find((component) => component.Name === draftComponent.componentName);

      if (!definition) {
        continue;
      }

      onProgress?.(`Adding ${draftComponent.displayName}`);

      let addedComponent: IContentComponent | null = null;

      if (isColumnTemplate(savedSection.Template)) {
        const targetColumn = Math.min(componentIndex, Math.max(savedSection.Columns - 1, 0));
        addedComponent = await this.contentEditorService.AddCComponentToColumnSection(definition, savedSection.Id, targetColumn);
      } else {
        const y = componentIndex * 10;
        addedComponent = await this.contentEditorService.AddComponentToGridSection(savedSection.Id, definition, 0, y, 12, 10);
      }

      if (!addedComponent) {
        continue;
      }

      const componentWithValues = cloneWithPropertyValues(addedComponent, draftComponent.propertyValues);
      await this.contentEditorService.SaveContentComponent(componentWithValues);
    }

    return {
      section: savedSection,
      draftSection
    };
  };
}
